import { FONT_FAMILIES } from '@dg-embalajes/catalog';
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Copy,
  Eye,
  EyeOff,
  FilePenLine,
  Lock,
  LocateFixed,
  Maximize,
  RotateCcw,
  Trash2,
  Unlock,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FONT_LONG_LABELS, STROKE_COLORS, STROKE_WIDTHS, TEXT_COLORS } from '@/content/copy';
import type { FontReadiness } from '@/hooks/useFontReadiness';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import type { AssetRole, EditorPort, EditorSnapshot } from '@/ports/editor';
import { isTextNode } from '@/ports/editor';

interface ElementPanelProps {
  readonly editor: EditorPort;
  readonly snapshot: EditorSnapshot;
  readonly editingTextId: string | null;
  readonly fonts: FontReadiness;
  readonly onEditingTextChange: (id: string | null) => void;
  readonly onReplaceAsset: (role: AssetRole) => void;
}

export function ElementPanel({ editor, snapshot, editingTextId, fonts, onEditingTextChange, onReplaceAsset }: ElementPanelProps) {
  const node = snapshot.selectedNode;
  const layer = snapshot.layers.find((item) => item.id === snapshot.selectedId);
  const viewport = useVisualViewport();
  const inputRef = useRef<HTMLInputElement>(null);
  const originalTextRef = useRef('');
  const finishingTextRef = useRef(false);
  const [textError, setTextError] = useState<string | null>(null);
  const editing = Boolean(node && layer && !layer.locked && node.id === editingTextId && isTextNode(node));

  useEffect(() => {
    if (editing) inputRef.current?.focus({ preventScroll: true });
  }, [editing]);

  if (!node || !layer) {
    return (
      <div className="empty-panel-state" data-testid="element-panel-empty">
        <span aria-hidden="true">Aa</span>
        <strong>Tocá un texto o imagen para editarlo</strong>
        <p>En Mesa los gestos mueven la vista. Al seleccionar un elemento, sus propiedades aparecen acá.</p>
      </div>
    );
  }

  const isCustomBackground = layer.kind === 'background';
  const isStickerImage = node.type === 'image' && node.role === 'sticker-image';
  const selectedQuality = snapshot.assetQuality.find((item) => item.nodeId === node.id);

  const beginTextEdit = () => {
    if (!isTextNode(node) || layer.locked) return;
    originalTextRef.current = node.text;
    setTextError(null);
    editor.beginTextEditing(node.id, viewport.height);
    onEditingTextChange(node.id);
  };

  const finishTextEdit = async () => {
    if (!isTextNode(node)) return;
    if (finishingTextRef.current) return;
    finishingTextRef.current = true;
    if (!node.text.trim()) {
      if (window.confirm('El texto está vacío. ¿Querés borrar este elemento?')) {
        await editor.endTextEditing(node.id);
        editor.delete(node.id);
        onEditingTextChange(null);
        finishingTextRef.current = false;
        return;
      }
      editor.updateText(node.id, { text: originalTextRef.current });
      setTextError('Se restauró el texto anterior.');
      finishingTextRef.current = false;
      return;
    }
    await editor.endTextEditing(node.id);
    onEditingTextChange(null);
    setTextError(null);
    finishingTextRef.current = false;
  };

  if (editing && isTextNode(node)) {
    return (
      <div className="text-edit-bar" data-testid="text-edit-bar">
        <label htmlFor="active-text-input">Editar texto</label>
        <input
          ref={inputRef}
          id="active-text-input"
          value={node.text}
          onChange={(event) => editor.updateText(node.id, { text: event.target.value })}
          onBlur={() => void finishTextEdit()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void finishTextEdit();
          }}
        />
        <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => void finishTextEdit()}>Listo</button>
        {textError ? <span role="alert">{textError}</span> : null}
      </div>
    );
  }

  return (
    <div className="panel-stack" data-testid="element-panel">
      <div className={`selected-element-heading ${layer.locked ? 'is-locked' : ''}`}>
        <span>{node.type === 'text' ? 'Aa' : node.role === 'pdf-art' ? 'PDF' : 'IMG'}</span>
        <div><small>Elemento seleccionado</small><strong>{layer.label}</strong></div>
        {layer.locked ? <Lock aria-label="Bloqueado" /> : null}
      </div>

      {layer.locked ? (
        <p className="locked-element-notice" role="status">
          Elemento bloqueado. Desbloquealo para editar, ajustar, reordenar o borrar. Podés seguir mostrándolo, ocultándolo o duplicándolo.
        </p>
      ) : null}

      {isTextNode(node) ? (
        <section className="property-section">
          <button className="primary-inline-action" type="button" disabled={layer.locked} onClick={beginTextEdit}><FilePenLine /> Editar texto</button>
          <label htmlFor="font-family">Tipografía</label>
          {fonts.status === 'error' ? (
            <p className="inline-error" role="alert">No se cargaron todas las tipografías ({fonts.missing.join(', ')}). No se reemplazarán silenciosamente.</p>
          ) : null}
          <select
            id="font-family"
            value={node.fontFamily}
            disabled={layer.locked || fonts.status !== 'ready'}
            onChange={(event) => editor.updateText(node.id, { fontFamily: event.target.value })}
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font.family} value={font.family}>{FONT_LONG_LABELS[font.family]} · {font.family}</option>
            ))}
          </select>

          <div className="field-control">
            <div className="field-control__label"><label htmlFor="font-size">Tamaño</label><output>{node.fontSizeMm.toFixed(1).replace('.', ',')} mm</output></div>
            <input id="font-size" type="range" min="3" max="40" step="0.5" value={node.fontSizeMm} disabled={layer.locked} onChange={(event) => editor.updateText(node.id, { fontSizeMm: Number(event.target.value) })} />
          </div>

          <fieldset className="swatch-fieldset" disabled={layer.locked}>
            <legend>Color</legend>
            <div className="swatch-grid" aria-label="Colores rápidos">
              {TEXT_COLORS.map((color) => (
                <button
                  key={color.name}
                  type="button"
                  className={node.fillColor.toLowerCase() === color.value.toLowerCase() ? 'is-selected' : ''}
                  title={color.name}
                  aria-label={color.name}
                  aria-pressed={node.fillColor.toLowerCase() === color.value.toLowerCase()}
                  style={{ '--swatch-color': color.value } as React.CSSProperties}
                  onClick={() => editor.updateText(node.id, { fillColor: color.value })}
                />
              ))}
            </div>
            <label className="color-input">Color personalizado<input type="color" value={node.fillColor} onChange={(event) => editor.updateText(node.id, { fillColor: event.target.value })} /></label>
          </fieldset>

          <div className="two-column-fields">
            <label>Contorno de texto
              <select value={node.stroke?.color ?? ''} disabled={layer.locked} onChange={(event) => editor.updateText(node.id, { strokeColor: event.target.value || null })}>
                {STROKE_COLORS.map((color) => <option key={color.name} value={color.value ?? ''}>{color.name}</option>)}
              </select>
            </label>
            <label>Grosor del contorno
              <select value={node.stroke?.widthMm ?? STROKE_WIDTHS[0].value} disabled={layer.locked || !node.stroke} onChange={(event) => editor.updateText(node.id, { strokeWidthMm: Number(event.target.value) })}>
                {STROKE_WIDTHS.map((width) => <option key={width.name} value={width.value}>{width.name}</option>)}
              </select>
            </label>
          </div>
          <small>Usalo cuando el patrón de fondo tapa la lectura del texto.</small>
        </section>
      ) : (
        <section className="property-section">
          {layer.canReplace ? (
            <button className="primary-inline-action" type="button" disabled={layer.locked} onClick={() => onReplaceAsset(isCustomBackground ? 'background' : node.role === 'pdf-art' ? 'pdf-art' : node.role)}><FilePenLine /> {isCustomBackground ? 'Reemplazar fondo' : 'Reemplazar archivo'}</button>
          ) : null}
          <p className="quality-line">{selectedQuality?.message ?? snapshot.qualityMessage}</p>
        </section>
      )}

      {isStickerImage ? (
        <section className="property-section">
          <div className="element-action-grid" aria-label="Ajuste de imagen del sticker">
            <button type="button" disabled={layer.locked} onClick={() => editor.centerImage(node.id)}><LocateFixed /> Centrar</button>
            <button type="button" disabled={layer.locked} onClick={() => editor.fitImage(node.id)}><Maximize /> Ajustar</button>
            <button type="button" disabled={layer.locked} onClick={() => editor.resetImage(node.id)}><RotateCcw /> Restablecer</button>
            <button className="danger-action" type="button" disabled={layer.locked} onClick={() => editor.delete(node.id)}><Trash2 /> Borrar imagen</button>
          </div>
        </section>
      ) : null}

      {isCustomBackground ? (
        <section className="property-section">
          <button className="primary-inline-action" type="button" disabled={layer.locked} onClick={() => editor.fitCustomBackgroundWidth()}><Maximize /> Ajustar imagen al ancho</button>
        </section>
      ) : null}

      <section className="property-section">
        <div className="field-control">
          <div className="field-control__label"><label htmlFor="element-scale">Escala</label><output>{Math.round(node.transform.scale * 100)}%</output></div>
          <input id="element-scale" type="range" min="0.1" max="5" step="0.05" value={node.transform.scale} disabled={layer.locked} onChange={(event) => editor.updateElementTransform(node.id, { scale: Number(event.target.value) })} />
        </div>
        <div className="field-control">
          <div className="field-control__label"><label htmlFor="element-rotation">Rotación</label><output>{Math.round(node.transform.rotationDeg)}°</output></div>
          <input id="element-rotation" type="range" min="-180" max="180" step="1" value={node.transform.rotationDeg} disabled={layer.locked} onChange={(event) => editor.updateElementTransform(node.id, { rotationDeg: Number(event.target.value) })} />
        </div>
      </section>

      <div className="element-action-grid">
        {layer.canMoveToFront ? <button type="button" disabled={layer.locked} onClick={() => editor.moveLayer(node.id, 'front')}><ArrowUpToLine /> Traer adelante</button> : null}
        {layer.canMoveToBack ? <button type="button" disabled={layer.locked} onClick={() => editor.moveLayer(node.id, 'back')}><ArrowDownToLine /> Mandar atrás</button> : null}
        {layer.canDuplicate ? <button type="button" onClick={() => editor.duplicate(node.id)}><Copy /> Duplicar</button> : null}
        {layer.canToggleVisibility ? <button type="button" onClick={() => editor.setVisible(node.id, !layer.visible)}>{layer.visible ? <EyeOff /> : <Eye />} {layer.visible ? 'Ocultar' : 'Mostrar'}</button> : null}
        {layer.canLock ? <button type="button" onClick={() => editor.setLocked(node.id, !layer.locked)}>{layer.locked ? <Unlock /> : <Lock />} {layer.locked ? 'Desbloquear' : 'Bloquear'}</button> : null}
        {layer.canDelete && !isStickerImage ? <button className="danger-action" type="button" disabled={layer.locked} onClick={() => editor.delete(node.id)}><Trash2 /> {isCustomBackground ? 'Quitar fondo' : 'Borrar'}</button> : null}
      </div>
      {!isStickerImage && !isCustomBackground ? <button className="reset-transform" type="button" disabled={layer.locked} onClick={() => editor.updateElementTransform(node.id, { scale: 1, rotationDeg: 0 })}><RotateCcw /> Restablecer escala y rotación</button> : null}
    </div>
  );
}
